import { useInView } from 'framer-motion';
import { Dispatch, RefObject, SetStateAction, useEffect, useMemo, useRef } from 'react';

import { BoxProps, rem, useToken } from '@vimeo/bokeh';
import { useLayout } from '@vimeo/clip-ui/hooks/use-layout';

import { useLiveChatStatus } from '../hooks/use-live-chat-status';
import { useAlbumStore } from '../state/albumStore';
import { NAV_HEIGHT } from '../utils/constants';

interface Props {
  /** he Clip Chat Room ID */
  roomId: number;
  /** The ID of the showcase the event is associated to. */
  associatedShowcaseId?: number;
  /** The ID of the event. */
  eventId?: string;
  /** Whether the event data is currently loading. */
  isEventLoading?: boolean;
  /** Reference to the player container element */
  playerContainerRef: RefObject<HTMLDivElement | null>;
  /** Function to set the interaction tools enabled state. */
  setIsInteractionToolsEnabled: Dispatch<SetStateAction<boolean>>;
}

/**
 * Hook to manage the state and behavior of the Live Engagement Tool module.
 * @param {Object} props - The properties for the hook.
 * @param {number} props.associatedShowcaseId - The ID of the showcase the event is associated to.
 * @param {string} props.eventId - The ID of the event.
 * @param {boolean} props.isEventLoading - Whether the event is currently loading.
 * @param {RefObject<HTMLDivElement | null>} props.playerContainerRef - Reference to the player container element.
 * @param {Dispatch<SetStateAction<boolean>>} props.setIsInteractionToolsEnabled - Function to set the interaction tools enabled state.
 * @returns - An object containing the state of the live tools side module and the room hashed password.
 */
export const useLiveEngagementToolModule = ({
  roomId,
  associatedShowcaseId,
  eventId,
  isEventLoading,
  playerContainerRef,
  setIsInteractionToolsEnabled,
}: Props) => {
  const { activeSideModule, activeMobileDrawer, isMobileDrawerOpen, onClose } = useLayout();
  const previousEventId = useRef<string | undefined>(eventId);

  const playerBottom = playerContainerRef.current?.getBoundingClientRect()?.bottom || 0;
  const isPlayerInView = useInView(playerContainerRef, { margin: `-${NAV_HEIGHT}px 0px 0px 0px` });
  const drawerTop = Math.max(playerBottom, NAV_HEIGHT);
  const zIndexDrawer = useToken('zindex', 'modal');

  const liveToolsMobileDrawerStyles: Partial<BoxProps> = useMemo(
    () => ({
      pos: 'fixed',
      top: isPlayerInView
        ? `calc(min(calc(${rem(drawerTop)} + ${rem(16)}), calc(100vh - ${rem(350)})))`
        : rem(drawerTop),
      bottom: 0,
      height: 'auto',
      zIndex: zIndexDrawer,
    }),
    [drawerTop, isPlayerInView, zIndexDrawer],
  );

  const { albumId, password } = useAlbumStore((state) => ({
    albumId: state.albumId,
    password: state.hashedPass,
  }));
  const roomHashedPassword = associatedShowcaseId === albumId ? password : undefined;
  const isLoading = !eventId || isEventLoading;

  const isLiveToolsSideModuleOpen =
    activeSideModule === 'LIVE_TOOLS' || activeMobileDrawer === 'LIVE_TOOLS';

  // Close the live engagement tools when eventId changes
  useEffect(() => {
    if (isLoading || previousEventId.current !== eventId) {
      onClose?.();
      previousEventId.current = eventId;
    }
  }, [eventId, isLoading, onClose]);

  // Prevent body scroll when the mobile drawer is open
  useEffect(() => {
    if (isMobileDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isMobileDrawerOpen]);

  const isInteractionToolsEnabled = useLiveChatStatus(roomId);

  // Close the live tools side module if interaction tools are disabled
  useEffect(() => {
    if (isLiveToolsSideModuleOpen && !isInteractionToolsEnabled) onClose?.();
  }, [isInteractionToolsEnabled, isLiveToolsSideModuleOpen, onClose]);

  useEffect(() => {
    setIsInteractionToolsEnabled(isInteractionToolsEnabled);
  }, [isInteractionToolsEnabled, setIsInteractionToolsEnabled]);

  return {
    isLiveToolsSideModuleOpen,
    liveToolsMobileDrawerStyles,
    roomHashedPassword,
  };
};
